import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { UserInfo } from "../utils/helper-interfaces";
import { devbackendUrl } from "../utils/constants";
import { AuthUtils } from "../utils/helper_functions";
import { clearAllGameContexts } from "../utils/active-game-context";


interface UserState {
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  userInfo: null,
  isLoading: false,
  error: null,
};
// Async thunk to fetch user data
export const fetchUser = createAsyncThunk('user/fetchUser', async (_, { rejectWithValue }) => {
  const token = AuthUtils.getAuthToken();
  if (!token) return rejectWithValue('No token provided');
  if(!AuthUtils.isTokenValid(token)){
    return rejectWithValue('Token is invalid');
  }
  try {
    const response = await fetch(`${devbackendUrl}user/info`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      throw new Error('Failed to fetch user data');
    }

    const data = await response.json();
    return {
      id: data.id,
      email: data.email,
      name: data.name,
      nickname: data.nickname,
      birthday: data.birthday,
      nationality: data.nationality,
      gender: data.gender,
      isConfirmed: data.isConfirmed,
      level: data.level,
      points: data.points,
      currency: data.currency,
      profileImageUrl: data.profileImageUrl,
      levelProgress: data.levelProgress,
    };
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.userInfo = null;
      localStorage.removeItem('token'); // Remove token if logging out
      clearAllGameContexts();
    },
    setUserInfo: (state, action) => {
      state.userInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userInfo = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logoutUser, setUserInfo } = userSlice.actions;
export default userSlice.reducer;
